<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import KanbanApi from 'dashboard/api/kanban';
import ConversationApi from 'dashboard/api/conversations';
import { dynamicTime } from 'shared/helpers/timeHelper';
// Reusa a MESMA janela de conversa do dashboard de Ofertas: moldura de
// iPhone, somente leitura, com atalho para abrir a conversa ao vivo.
import ConversaModal from '../segueDashboard/components/ConversaModal.vue';

const router = useRouter();
const { accountScopedRoute } = useAccount();

// Espiar a conversa SEM sair do quadro: o card inteiro continua indo para
// a conversa; o ícone abre só a leitura.
const conversaPhone = ref('');
const conversaNome = ref('');
const espiarConversa = conv => {
  conversaPhone.value = conv.phone || '';
  conversaNome.value = conv.name || '';
};

const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value?.id === 1);

// v172 (Kairo 19/07): o Kanban abre SEM carregar nada — só os seletores.
// /kanban/filtros devolve o que o papel do usuário pode ver (caixas, times,
// usuários por papel); o board só é buscado depois de escolher a CAIXA.
const filtros = ref({ papel: '', caixas: [], times: [], usuarios_por_papel: {} });
const dataSel = ref(''); // '' = DATA: TODOS
const modoData = ref('criadas'); // criadas | janela24h (só vale com dataSel)
const fechadas = ref(false); // Exibir Conversas Fechadas
const visualizar = ref('todos'); // todos | time | agente
const timeSel = ref(null);
const roleSel = ref(null); // papel escolhido no modo AGENTE (admin)
const agenteSel = ref(null); // { id, nome }
const visualizarMenuOpen = ref(false);

// tooltips LITERAIS do Kairo
const TOOLTIP_CRIADAS =
  'Visualizar clientes novos que entraram nessa Data pela primeira vez no atendimento.';
const TOOLTIP_JANELA =
  'Visualizar clientes novos e antigos que estão em atendimento dentro da janela permitida de 24 horas da Meta.';

const papel = computed(() => filtros.value.papel);
const podeTime = computed(() => papel.value === 'administrator');
const podeAgente = computed(() =>
  ['administrator', 'supervisor'].includes(papel.value)
);
const podeVisualizar = computed(() => podeTime.value || podeAgente.value);
const rolesDisponiveis = computed(() =>
  Object.keys(filtros.value.usuarios_por_papel || {})
);
const usuariosDoRole = computed(() =>
  roleSel.value ? filtros.value.usuarios_por_papel?.[roleSel.value] || [] : []
);

const visualizarLabel = computed(() => {
  if (visualizar.value === 'time' && timeSel.value)
    return `TIME: ${filtros.value.times.find(t => t.id === timeSel.value)?.nome || ''}`;
  if (visualizar.value === 'agente' && agenteSel.value)
    return `AGENTE: ${agenteSel.value.nome}`;
  return 'VISUALIZAR: TODOS';
});

const filtrosAtuais = () => ({
  inboxId: selectedInboxId.value,
  data: dataSel.value || null,
  modoData: modoData.value,
  fechadas: fechadas.value,
  teamId: visualizar.value === 'time' ? timeSel.value : null,
  assigneeId: visualizar.value === 'agente' ? agenteSel.value?.id : null,
});

// Mesmas regras do backend (labelable.rb):
// - marcadoras coexistem com o status -> não são colunas de arrasto (nem origem
//   nem destino); o card delas também vive numa coluna de status.
// - protegidas só o super admin (id 1) remove -> só ele arrasta PRA FORA delas.
const MARKER_LABELS = [
  'clt_link_simule_enviado',
  'clt_pagamento_efetuado',
  'clt_operacional',
];
const PROTECTED_LABELS = ['clt_pago', 'clt_pagamento_efetuado'];

const columns = ref([]);
const pageSize = ref(25);
const isLoading = ref(false);
const loadingMore = ref({});
const selectedInboxId = ref(null);
const inboxMenuOpen = ref(false);

// Drag & drop + modal de confirmação
const dragging = ref(null); // { conv, from }
const dropTarget = ref(null); // title da coluna sob o cursor
const confirmMove = ref(null); // { conv, from, to, saving }

const selectedInboxName = computed(() => {
  if (!selectedInboxId.value) return 'ESCOLHA A CAIXA';
  const i = filtros.value.caixas.find(x => x.id === selectedInboxId.value);
  return i?.nome || 'ESCOLHA A CAIXA';
});

const totalCards = computed(() =>
  columns.value.reduce((sum, c) => sum + c.total, 0)
);

const canDragFrom = title =>
  !MARKER_LABELS.includes(title) &&
  (isSuperAdmin.value || !PROTECTED_LABELS.includes(title));

const canDropTo = title => !MARKER_LABELS.includes(title);

const fetchBoard = async () => {
  // v172: sem caixa escolhida NÃO consulta nada (abre leve)
  if (!selectedInboxId.value) {
    columns.value = [];
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await KanbanApi.getBoard(filtrosAtuais());
    columns.value = data.columns || [];
    pageSize.value = data.page_size || 25;
  } catch {
    useAlert('Não foi possível carregar o Kanban.');
  } finally {
    isLoading.value = false;
  }
};

const loadMore = async column => {
  if (loadingMore.value[column.title]) return;
  loadingMore.value = { ...loadingMore.value, [column.title]: true };
  try {
    const { data } = await KanbanApi.getColumn({
      label: column.title,
      offset: column.conversations.length,
      ...filtrosAtuais(),
    });
    const seen = new Set(column.conversations.map(c => c.id));
    const novos = (data.conversations || []).filter(c => !seen.has(c.id));
    column.conversations.push(...novos);
  } catch {
    useAlert('Não foi possível carregar mais leads.');
  } finally {
    loadingMore.value = { ...loadingMore.value, [column.title]: false };
  }
};

const selectInbox = id => {
  selectedInboxId.value = id;
  inboxMenuOpen.value = false;
  fetchBoard();
};

const toggleInboxMenu = () => {
  inboxMenuOpen.value = !inboxMenuOpen.value;
};

const closeInboxMenu = e => {
  if (!e.target.closest('.kanban-inbox-filter')) {
    inboxMenuOpen.value = false;
  }
  if (!e.target.closest('.kanban-visualizar-filter')) {
    visualizarMenuOpen.value = false;
  }
};

// ---- v172: handlers dos filtros ----
const onDataChange = () => {
  if (!dataSel.value) modoData.value = 'criadas';
  fetchBoard();
};

const limparData = () => {
  dataSel.value = '';
  modoData.value = 'criadas';
  fetchBoard();
};

const setModoData = modo => {
  modoData.value = modo;
  fetchBoard();
};

const toggleFechadas = () => {
  fechadas.value = !fechadas.value;
  fetchBoard();
};

const setVisualizarTodos = () => {
  visualizar.value = 'todos';
  timeSel.value = null;
  roleSel.value = null;
  agenteSel.value = null;
  visualizarMenuOpen.value = false;
  fetchBoard();
};

const selectTime = id => {
  visualizar.value = 'time';
  timeSel.value = id;
  agenteSel.value = null;
  visualizarMenuOpen.value = false;
  fetchBoard();
};

const selectAgente = usuario => {
  visualizar.value = 'agente';
  agenteSel.value = usuario;
  timeSel.value = null;
  visualizarMenuOpen.value = false;
  fetchBoard();
};

const openConversation = conv => {
  router.push(
    accountScopedRoute('inbox_conversation', { conversation_id: conv.id })
  );
};

// --- Drag & drop ---
const onDragStart = (e, conv, fromTitle) => {
  dragging.value = { conv, from: fromTitle };
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('text/plain', String(conv.id));
};

const onDragEnd = () => {
  dragging.value = null;
  dropTarget.value = null;
};

const onDragEnter = title => {
  if (!dragging.value) return;
  dropTarget.value =
    title !== dragging.value.from && canDropTo(title) ? title : null;
};

const onDrop = title => {
  const drag = dragging.value;
  dropTarget.value = null;
  if (!drag || title === drag.from || !canDropTo(title)) return;
  confirmMove.value = {
    conv: drag.conv,
    from: drag.from,
    to: title,
    saving: false,
  };
};

const cancelMove = () => {
  confirmMove.value = null;
};

const applyMove = async () => {
  const move = confirmMove.value;
  if (!move || move.saving) return;
  move.saving = true;
  try {
    // Envia só a etiqueta nova: o backend (update_labels) troca o status e
    // preserva as marcadoras; a atividade fica registrada no chat da conversa.
    await ConversationApi.updateLabels(move.conv.id, [move.to]);
    const fromCol = columns.value.find(c => c.title === move.from);
    const toCol = columns.value.find(c => c.title === move.to);
    if (fromCol) {
      fromCol.conversations = fromCol.conversations.filter(
        c => c.id !== move.conv.id
      );
      fromCol.total = Math.max(0, fromCol.total - 1);
    }
    if (toCol && !toCol.conversations.some(c => c.id === move.conv.id)) {
      toCol.conversations.unshift(move.conv);
      toCol.total += 1;
    }
    columns.value = columns.value.filter(c => c.total > 0);
    useAlert(`Status alterado: ${move.from} → ${move.to}`);
    confirmMove.value = null;
  } catch {
    useAlert('Não foi possível alterar o status. Tente novamente.');
    move.saving = false;
  }
};

const enteredAt = conv =>
  conv.last_activity_at ? dynamicTime(conv.last_activity_at) : '';

const initials = name =>
  (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

onMounted(async () => {
  document.addEventListener('click', closeInboxMenu);
  // v172: só os SELETORES carregam ao abrir — o board espera a caixa.
  try {
    const { data } = await KanbanApi.getFiltros();
    filtros.value = data || filtros.value;
  } catch {
    useAlert('Não foi possível carregar os filtros do Kanban.');
  }
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center justify-between px-6 py-3 border-b border-n-weak bg-n-solid-1"
    >
      <div class="flex items-center gap-3">
        <span class="i-lucide-kanban-square size-5 text-n-brand" />
        <h1 class="text-lg font-semibold text-n-slate-12">Kanban</h1>
        <span
          v-if="!isLoading"
          class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
        >
          {{ columns.length }} etiquetas · {{ totalCards.toLocaleString('pt-BR') }} leads
        </span>
      </div>

      <div class="flex items-center gap-2">
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-md border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 transition-colors"
          @click="fetchBoard"
        >
          <span
            class="i-lucide-refresh-cw size-4 text-n-slate-11"
            :class="{ 'animate-spin': isLoading }"
          />
          Atualizar
        </button>

        <div class="kanban-inbox-filter relative">
          <button
            type="button"
            class="flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-md border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 transition-colors"
            @click="toggleInboxMenu"
          >
            <span class="i-lucide-mailbox size-4 text-n-slate-11" />
            <span>{{ selectedInboxName }}</span>
            <span class="i-lucide-chevron-down size-4 text-n-slate-11" />
          </button>
          <div
            v-if="inboxMenuOpen"
            class="absolute right-0 z-10 mt-2 overflow-y-auto rounded-md shadow-lg bg-n-solid-1 border border-n-weak min-w-[260px] max-h-[400px]"
          >
            <button
              v-for="inbox in filtros.caixas"
              :key="inbox.id"
              type="button"
              class="block w-full px-4 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              :class="{
                'bg-n-alpha-2 font-semibold': selectedInboxId === inbox.id,
              }"
              @click="selectInbox(inbox.id)"
            >
              {{ inbox.nome }}
            </button>
          </div>
        </div>

        <!-- v172: DATA (TODOS ou dia + modo com tooltip) -->
        <div class="flex items-center gap-1.5">
          <span class="text-xs font-semibold text-n-slate-11">DATA:</span>
          <input
            v-model="dataSel"
            type="date"
            class="px-2 py-1.5 text-sm border rounded-md border-n-weak bg-n-alpha-1 text-n-slate-12"
            @change="onDataChange"
          />
          <button
            v-if="dataSel"
            type="button"
            class="px-2 py-1.5 text-xs font-medium border rounded-md border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
            title="Voltar para DATA: TODOS"
            @click="limparData"
          >
            TODOS
          </button>
          <template v-if="dataSel">
            <button
              type="button"
              class="px-2.5 py-1.5 text-xs font-semibold border rounded-md transition-colors"
              :class="
                modoData === 'criadas'
                  ? 'bg-n-brand text-white border-n-brand'
                  : 'border-n-weak text-n-slate-12 hover:bg-n-alpha-2'
              "
              :title="TOOLTIP_CRIADAS"
              @click="setModoData('criadas')"
            >
              CONVERSAS CRIADAS NO DIA
            </button>
            <button
              type="button"
              class="px-2.5 py-1.5 text-xs font-semibold border rounded-md transition-colors"
              :class="
                modoData === 'janela24h'
                  ? 'bg-n-brand text-white border-n-brand'
                  : 'border-n-weak text-n-slate-12 hover:bg-n-alpha-2'
              "
              :title="TOOLTIP_JANELA"
              @click="setModoData('janela24h')"
            >
              JANELA 24 HORAS
            </button>
          </template>
        </div>

        <!-- v172: Exibir Conversas Fechadas -->
        <button
          type="button"
          class="px-3 py-2 text-sm font-medium border rounded-md transition-colors"
          :class="
            fechadas
              ? 'bg-n-teal-9 text-white border-n-teal-9'
              : 'border-n-weak bg-n-alpha-1 text-n-slate-12 hover:bg-n-alpha-2'
          "
          title="Incluir as conversas fechadas (resolvidas) no Kanban"
          @click="toggleFechadas"
        >
          Exibir Conversas Fechadas
        </button>

        <!-- v172: VISUALIZAR (admin: TODOS/TIME/AGENTE; supervisor: TODOS/AGENTE do time) -->
        <div v-if="podeVisualizar" class="kanban-visualizar-filter relative">
          <button
            type="button"
            class="flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-md border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 transition-colors"
            @click="visualizarMenuOpen = !visualizarMenuOpen"
          >
            <span class="i-lucide-users size-4 text-n-slate-11" />
            <span>{{ visualizarLabel }}</span>
            <span class="i-lucide-chevron-down size-4 text-n-slate-11" />
          </button>
          <div
            v-if="visualizarMenuOpen"
            class="absolute right-0 z-10 mt-2 overflow-y-auto rounded-md shadow-lg bg-n-solid-1 border border-n-weak min-w-[280px] max-h-[420px]"
          >
            <button
              type="button"
              class="block w-full px-4 py-2 text-sm font-semibold text-left text-n-slate-12 hover:bg-n-alpha-2"
              :class="{ 'bg-n-alpha-2': visualizar === 'todos' }"
              @click="setVisualizarTodos"
            >
              TODOS
            </button>
            <template v-if="podeTime && filtros.times.length">
              <div class="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-n-slate-10 border-t border-n-weak">
                Time
              </div>
              <button
                v-for="t in filtros.times"
                :key="`t${t.id}`"
                type="button"
                class="block w-full px-6 py-1.5 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
                :class="{ 'bg-n-alpha-2 font-semibold': visualizar === 'time' && timeSel === t.id }"
                @click="selectTime(t.id)"
              >
                {{ t.nome }}
              </button>
            </template>
            <template v-if="podeAgente && rolesDisponiveis.length">
              <div class="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-n-slate-10 border-t border-n-weak">
                Agente (por perfil)
              </div>
              <button
                v-for="r in rolesDisponiveis"
                :key="`r${r}`"
                type="button"
                class="block w-full px-6 py-1.5 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
                :class="{ 'bg-n-alpha-2 font-semibold': roleSel === r }"
                @click.stop="roleSel = roleSel === r ? null : r"
              >
                {{ r }} ▸
              </button>
              <template v-if="roleSel">
                <button
                  v-for="u in usuariosDoRole"
                  :key="`u${u.id}`"
                  type="button"
                  class="block w-full px-9 py-1.5 text-sm text-left text-n-slate-11 hover:bg-n-alpha-2"
                  :class="{ 'bg-n-alpha-2 font-semibold text-n-slate-12': agenteSel?.id === u.id }"
                  @click="selectAgente(u)"
                >
                  {{ u.nome }}
                </button>
              </template>
            </template>
          </div>
        </div>
      </div>
    </header>

    <!-- v172: nada carrega até escolher a caixa -->
    <div
      v-if="!selectedInboxId"
      class="flex flex-col items-center justify-center flex-1 gap-3 text-n-slate-10"
    >
      <span class="i-lucide-mailbox size-10 text-n-slate-8" />
      <p class="text-base font-semibold text-n-slate-11">ESCOLHA A CAIXA</p>
      <p class="text-sm">
        Selecione uma caixa no topo para carregar o Kanban dela.
      </p>
    </div>

    <div
      v-else-if="isLoading"
      class="flex items-center justify-center flex-1 gap-2 text-sm text-n-slate-10"
    >
      <span class="i-lucide-loader-2 size-4 animate-spin" />
      Carregando leads…
    </div>

    <div
      v-else-if="!columns.length"
      class="flex items-center justify-center flex-1 text-sm text-n-slate-10"
    >
      Nenhum lead com etiqueta encontrado.
    </div>

    <div v-else class="flex-1 px-5 py-4 overflow-x-auto">
      <div class="flex h-full gap-3">
        <section
          v-for="column in columns"
          :key="column.title"
          class="flex flex-col w-72 overflow-hidden rounded-xl shrink-0 border bg-n-solid-1 transition-shadow"
          :class="
            dropTarget === column.title
              ? 'border-n-brand shadow-lg'
              : 'border-n-weak'
          "
          @dragover.prevent
          @dragenter.prevent="onDragEnter(column.title)"
          @drop.prevent="onDrop(column.title)"
        >
          <div
            class="h-1 shrink-0"
            :style="{ backgroundColor: column.color || '#1f93ff' }"
          />
          <div class="flex items-center justify-between gap-2 px-3 py-2.5">
            <div class="flex items-center min-w-0 gap-2">
              <span
                class="rounded-full size-2.5 shrink-0"
                :style="{ backgroundColor: column.color || '#1f93ff' }"
              />
              <span
                class="text-xs font-semibold tracking-wide uppercase truncate text-n-slate-12"
              >
                {{ column.title.replace(/^clt_/, '').replaceAll('_', ' ') }}
              </span>
              <span
                v-if="!canDragFrom(column.title)"
                class="i-lucide-lock size-3 shrink-0 text-n-slate-9"
                title="Etiqueta protegida ou marcadora — sem arrasto"
              />
            </div>
            <span
              class="px-2 py-0.5 text-xs font-bold rounded-full bg-n-alpha-2 text-n-slate-11 shrink-0"
            >
              {{ column.total.toLocaleString('pt-BR') }}
            </span>
          </div>

          <div class="flex-1 px-2 pb-2 space-y-2 overflow-y-auto">
            <article
              v-for="conv in column.conversations"
              :key="conv.id"
              class="p-3 transition-all border rounded-lg group bg-n-solid-2 border-n-weak hover:border-n-slate-7 hover:shadow-sm"
              :class="[
                canDragFrom(column.title) ? 'cursor-grab' : 'cursor-pointer',
                dragging?.conv?.id === conv.id ? 'opacity-40' : '',
              ]"
              :draggable="canDragFrom(column.title)"
              @dragstart="onDragStart($event, conv, column.title)"
              @dragend="onDragEnd"
              @click="openConversation(conv)"
            >
              <div class="flex items-center gap-2.5">
                <span
                  class="flex items-center justify-center text-[11px] font-bold text-white rounded-full size-8 shrink-0"
                  :style="{ backgroundColor: column.color || '#1f93ff' }"
                >
                  {{ initials(conv.name) }}
                </span>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-semibold truncate text-n-slate-12">
                    {{ conv.name }}
                  </div>
                  <div
                    v-if="conv.phone"
                    class="text-xs truncate text-n-slate-11"
                  >
                    {{ conv.phone }}
                  </div>
                </div>
                <!-- Espiar a conversa sem sair do quadro. Discreto até o hover:
                     o card não pode virar um mural de botões. -->
                <button
                  v-if="conv.phone"
                  type="button"
                  class="kwhats"
                  title="Ver a conversa inteira"
                  @click.stop="espiarConversa(conv)"
                >
                  <span class="i-lucide-message-circle size-4" />
                </button>
              </div>

              <div
                class="flex items-center justify-between mt-2 text-[11px] text-n-slate-10"
              >
                <span v-if="enteredAt(conv)" class="flex items-center gap-1">
                  <span class="i-lucide-clock size-3" />
                  {{ enteredAt(conv) }}
                </span>
                <span class="flex items-center gap-2">
                  <span
                    v-if="conv.assignee"
                    class="truncate max-w-[100px]"
                    :title="conv.assignee"
                  >
                    {{ conv.assignee }}
                  </span>
                  <span class="font-medium">#{{ conv.id }}</span>
                </span>
              </div>
            </article>

            <button
              v-if="column.conversations.length < column.total"
              type="button"
              class="flex items-center justify-center w-full gap-1.5 px-3 py-2 text-xs font-medium border border-dashed rounded-lg border-n-weak text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
              @click="loadMore(column)"
            >
              <span
                v-if="loadingMore[column.title]"
                class="i-lucide-loader-2 size-3.5 animate-spin"
              />
              <span v-else class="i-lucide-chevron-down size-3.5" />
              Carregar mais
              ({{ (column.total - column.conversations.length).toLocaleString('pt-BR') }} restantes)
            </button>
          </div>
        </section>
      </div>
    </div>

    <!-- Modal de confirmação do arrasto -->
    <div
      v-if="confirmMove"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="cancelMove"
    >
      <div
        class="w-full max-w-md p-6 mx-4 rounded-xl shadow-2xl bg-n-solid-1 border border-n-weak"
      >
        <div class="flex items-center gap-3">
          <span
            class="flex items-center justify-center rounded-full size-10 bg-n-alpha-2 shrink-0"
          >
            <span class="i-lucide-arrow-right-left size-5 text-n-brand" />
          </span>
          <h2 class="text-base font-semibold text-n-slate-12">
            Tem certeza que deseja alterar o Status desse cliente?
          </h2>
        </div>

        <div class="mt-4 text-sm text-n-slate-11">
          <div class="font-semibold text-n-slate-12">
            {{ confirmMove.conv.name }}
            <span class="font-normal text-n-slate-10">
              · #{{ confirmMove.conv.id }}
            </span>
          </div>
          <div class="flex items-center gap-2 mt-2">
            <span
              class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
            >
              {{ confirmMove.from }}
            </span>
            <span class="i-lucide-arrow-right size-3.5 text-n-slate-10" />
            <span
              class="px-2 py-0.5 text-xs font-medium text-white rounded-full bg-n-brand"
            >
              {{ confirmMove.to }}
            </span>
          </div>
        </div>

        <div class="flex justify-between gap-3 mt-6">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium border rounded-md border-n-weak text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            :disabled="confirmMove.saving"
            @click="cancelMove"
          >
            NÃO
          </button>
          <button
            type="button"
            class="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-md bg-n-brand hover:brightness-110 transition-all disabled:opacity-60"
            :disabled="confirmMove.saving"
            @click="applyMove"
          >
            <span
              v-if="confirmMove.saving"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            SIM
          </button>
        </div>
      </div>
    </div>

    <!-- Conversa em moldura de iPhone, igual ao dashboard de Ofertas. -->
    <ConversaModal
      v-if="conversaPhone"
      :phone="conversaPhone"
      :nome="conversaNome"
      @close="conversaPhone = ''"
      @navigate="conversaPhone = ''"
    />
  </div>
</template>

<style scoped>
/* Botão de conversa do card: discreto em repouso, verde do WhatsApp no hover.
   Sem isto ele competiria visualmente com o nome do cliente, que é a
   informação principal do card. */
.kwhats {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 9px;
  color: #64748b;
  background: rgba(148, 163, 184, 0.14);
  opacity: 0.55;
  transition: all 0.15s ease;
}
.group:hover .kwhats { opacity: 1; }
.kwhats:hover {
  color: #fff;
  background: #25d366;
  transform: scale(1.08);
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.35);
}
.kwhats:active { transform: scale(0.96); }
</style>
